import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import {
  checkTodayAttendance,
  checkIn,
  checkOut,
  formatCheckInTime,
  getTotalWorkedHours
} from '../../../services/attendanceService'
import {
  getTotalAllocatedHours,
  getTodayDate,
  getPendingTimesheetsCount
} from '../../../services/timesheetService'
import {
  createRequest,
  getApprovedLeaveDays,
  getCustomLeaveBalance,
  getPendingRequestsCount
} from '../../../services/timeOffService'
import { getRecentAnnouncementsCount } from '../../../services/announcementsService'

// Import components
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import Badge from '../../../components/common/Badge/Badge'
import RequestModal from '../../time-off/components/RequestModal'
import EditLeaveBalanceModal from '../components/EditLeaveBalanceModal'

const Dashboard = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)

  // Clock In/Out state
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [checkInTime, setCheckInTime] = useState(null)
  const [hasCheckedOut, setHasCheckedOut] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false)

  // Weekly hours tracking
  const [weeklyHours, setWeeklyHours] = useState(0)
  const [weeklyWorkedHours, setWeeklyWorkedHours] = useState(0)
  const [isLoadingWeeklyHours, setIsLoadingWeeklyHours] = useState(false)

  // Dashboard stats
  const [leaveBalance, setLeaveBalance] = useState(15)
  const [isLoadingLeaveBalance, setIsLoadingLeaveBalance] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  const [isLoadingPending, setIsLoadingPending] = useState(false)
  const [announcementsCount, setAnnouncementsCount] = useState(0)
  const [isLoadingAnnouncements, setIsLoadingAnnouncements] = useState(false)
  const [isEditLeaveModalOpen, setIsEditLeaveModalOpen] = useState(false)

  useEffect(() => {
    const currentUser = getCurrentUser()
    if (currentUser) {
      setUser(currentUser)

      // Get user data from Firestore
      getUserData(currentUser.uid).then((result) => {
        if (result.success) {
          setUserData(result.data)

          // Check today's attendance
          checkTodayAttendanceStatus(result.data.employeeId)

          // Load dashboard stats
          loadWeeklyHours(result.data.employeeId)
          loadLeaveBalance(result.data)
          loadPendingCount(result.data)
          loadAnnouncementsCount()
        }
      })
    }
  }, [])

  // Helper: Get this week's dates (Monday-Sunday)
  const getThisWeekDates = () => {
    const today = new Date()
    const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const monday = new Date(today)

    // Calculate days to subtract to get to Monday
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    monday.setDate(today.getDate() - daysToMonday)

    const dates = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      dates.push(date.toISOString().split('T')[0])
    }

    return dates
  }

  const checkTodayAttendanceStatus = async (employeeId) => {
    const result = await checkTodayAttendance(employeeId)
    if (result.success && result.data.isCheckedIn) {
      setIsCheckedIn(true)
      setCheckInTime(result.data.checkIn)
      setHasCheckedOut(result.data.hasCheckedOut)
    }
  }

  const loadWeeklyHours = async (employeeId) => {
    setIsLoadingWeeklyHours(true)

    try {
      const weekDates = getThisWeekDates()
      let totalWorkedHours = 0
      let totalAllocatedHours = 0

      // Loop through each day of the week
      for (const date of weekDates) {
        // Get worked hours from attendance
        const workedResult = await getTotalWorkedHours(employeeId, date)
        if (workedResult.success && workedResult.hasCheckOut) {
          totalWorkedHours += workedResult.hours
        }

        // Get allocated hours from timesheet
        const allocatedResult = await getTotalAllocatedHours(employeeId, date)
        if (allocatedResult.success) {
          totalAllocatedHours += allocatedResult.hours
        }
      }

      setWeeklyWorkedHours(totalWorkedHours)
      setWeeklyHours(totalAllocatedHours)
    } catch (error) {
      console.error('Error loading weekly hours:', error)
    } finally {
      setIsLoadingWeeklyHours(false)
    }
  }

  const loadLeaveBalance = async (userInfo) => {
    if (!userInfo) return

    const { employeeId, uid } = userInfo
    if (!employeeId) return

    setIsLoadingLeaveBalance(true)

    try {
      const docUid = uid || user?.uid || null

      // 1. Check for custom balance first
      const customBalance = await getCustomLeaveBalance(employeeId, docUid)
      if (customBalance !== null) {
        setLeaveBalance(customBalance)
        setIsLoadingLeaveBalance(false)
        return
      }

      // 2. Calculate from approved days
      const approvedDays = await getApprovedLeaveDays(employeeId)
      const balance = Math.max(0, 15 - approvedDays)
      setLeaveBalance(balance)
    } catch (error) {
      console.error('Error loading leave balance:', error)
    } finally {
      setIsLoadingLeaveBalance(false)
    }
  }

  const loadPendingCount = async (userData) => {
    setIsLoadingPending(true)

    try {
      // Get counts from both collections
      const timeOffCount = await getPendingRequestsCount(userData)
      const timesheetCount = await getPendingTimesheetsCount(userData)

      setPendingCount(timeOffCount + timesheetCount)
    } catch (error) {
      console.error('Error loading pending count:', error)
    } finally {
      setIsLoadingPending(false)
    }
  }

  const loadAnnouncementsCount = async () => {
    setIsLoadingAnnouncements(true)

    try {
      const result = await getRecentAnnouncementsCount()
      if (result.success) {
        setAnnouncementsCount(result.count)
      }
    } catch (error) {
      console.error('Error loading announcements count:', error)
    } finally {
      setIsLoadingAnnouncements(false)
    }
  }

  const handleCheckIn = async () => {
    if (!userData) return

    setIsLoading(true)
    setError('')
    setSuccessMessage('')

    const result = await checkIn(userData.employeeId, user.uid)

    if (result.success) {
      setIsCheckedIn(true)
      setCheckInTime(result.data.checkIn)
      setHasCheckedOut(false)
      setSuccessMessage(`Checked in successfully at ${formatCheckInTime(result.data.checkIn)}!`)
      setTimeout(() => setSuccessMessage(''), 3000)
    } else {
      setError(result.error || 'Failed to check in. Please try again.')
    }

    setIsLoading(false)
  }

  const handleCheckOut = async () => {
    if (!userData) return

    setIsLoading(true)
    setError('')
    setSuccessMessage('')

    const result = await checkOut(userData.employeeId)

    if (result.success) {
      setHasCheckedOut(true)
      setSuccessMessage(`Checked out successfully at ${formatCheckInTime(result.data.checkOut)}!`)
      setTimeout(() => setSuccessMessage(''), 3000)
    } else {
      setError(result.error || 'Failed to check out. Please try again.')
    }

    setIsLoading(false)
  }

  const handleOpenRequestModal = () => {
    setError('')
    setSuccessMessage('')
    setIsRequestModalOpen(true)
  }

  const handleCloseRequestModal = () => {
    if (!isSubmittingRequest) {
      setIsRequestModalOpen(false)
    }
  }

  const handleSubmitTimeOffRequest = async (formData) => {
    if (!user || !userData) {
      setError('User information is not available. Please try again.')
      setTimeout(() => setError(''), 5000)
      return
    }

    setIsSubmittingRequest(true)
    setError('')
    setSuccessMessage('')

    try {
      const result = await createRequest(formData, {
        employeeId: userData.employeeId,
        uid: user.uid,
        fullName: userData.fullName,
        email: userData.email
      })

      if (result.success) {
        setSuccessMessage('Time off request submitted successfully!')
        setIsRequestModalOpen(false)
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to submit time off request. Please try again.')
        setTimeout(() => setError(''), 5000)
      }
    } catch (requestError) {
      console.error('Time off request error:', requestError)
      setError('Failed to submit time off request. Please try again.')
      setTimeout(() => setError(''), 5000)
    } finally {
      setIsSubmittingRequest(false)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      navigate('/login')
    }
  }

  const handleLeaveBalanceUpdated = () => {
    // Reload all dashboard stats after leave balance update
    if (userData) {
      loadLeaveBalance(userData)
      loadPendingCount(userData)
      loadAnnouncementsCount()
    }
  }

  // Module definitions
  const activeModules = [
    {
      id: 'timesheet',
      title: 'Timesheet',
      icon: 'fas fa-business-time',
      description: 'Track and manage your work hours',
      route: '/timesheet'
    },
    {
      id: 'time-off',
      title: 'Time Off Requests',
      icon: 'fas fa-calendar-check',
      description: 'Request and manage time off',
      route: '/time-off'
    },
    {
      id: 'salary-slips',
      title: 'Salary Slips',
      icon: 'fas fa-file-invoice-dollar',
      description: 'View and download salary slips',
      route: '/salary-slips'
    },
    {
      id: 'announcements',
      title: 'Announcements',
      icon: 'fas fa-bullhorn',
      description: 'View company announcements',
      route: '/announcements'
    },
    {
      id: 'leave-policy',
      title: 'Leave Policy',
      icon: 'fas fa-book-open',
      description: 'View leave policies and guidelines',
      route: '/leave-policy'
    },
    {
      id: 'holidays',
      title: 'Holidays',
      icon: 'fas fa-calendar-star',
      description: 'View company holiday calendar',
      route: '/holidays'
    },
    {
      id: 'attendance',
      title: 'Attendance Records',
      icon: 'fas fa-user-clock',
      description: 'View and manage attendance records',
      route: '/attendance'
    }
  ]

  const comingSoonModules = [
    {
      id: 'performance',
      title: 'Performance',
      icon: 'fas fa-chart-line',
      description: 'Track your performance reviews'
    },
    {
      id: 'benefits',
      title: 'Benefits',
      icon: 'fas fa-heart-pulse',
      description: 'View employee benefits'
    },
    {
      id: 'directory',
      title: 'Directory',
      icon: 'fas fa-address-book',
      description: 'Search employee directory'
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: 'fas fa-folder-open',
      description: 'Access company documents'
    },
    {
      id: 'expenses',
      title: 'Expenses',
      icon: 'fas fa-receipt',
      description: 'Submit expense claims'
    },
    {
      id: 'it-help',
      title: 'IT Help',
      icon: 'fas fa-headset',
      description: 'Get IT support and help'
    },
    {
      id: 'learning',
      title: 'Learning',
      icon: 'fas fa-graduation-cap',
      description: 'Access training materials'
    },
    {
      id: 'products',
      title: 'Products',
      icon: 'fas fa-box-open',
      description: 'Browse company products'
    }
  ]

  return (
    <div className="psyber-container psyber-portal-container">
      <BackgroundEffects />

      <Header
        user={userData || user}
        onLogout={handleLogout}
        title="PsyberNova Portal"
      />

      {/* Error/Success Messages */}
      {error && (
        <Card style={{
          marginBottom: '1.5rem',
          background: 'var(--psyber-error-bg)',
          border: '2px solid var(--psyber-error)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</p>
          </div>
        </Card>
      )}

      {successMessage && (
        <Card style={{
          marginBottom: '1.5rem',
          background: 'var(--psyber-success-bg)',
          border: '2px solid var(--psyber-success)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-check-circle" style={{ color: 'var(--psyber-success)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-success)', fontWeight: 600 }}>{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Quick Actions Panel */}
      <Card style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-bolt"></i> Quick Actions
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          {/* Clock In/Out Button - Primary Action */}
          {!isCheckedIn ? (
            <Button
              variant="primary"
              icon="fas fa-clock"
              onClick={handleCheckIn}
              loading={isLoading}
              disabled={isLoading}
            >
              Check In
            </Button>
          ) : hasCheckedOut ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem 1.5rem',
              background: 'var(--psyber-success-bg)',
              border: '2px solid var(--psyber-success)',
              borderRadius: 'var(--psyber-radius-md)',
              fontWeight: 600,
              color: 'var(--psyber-success)'
            }}>
              <i className="fas fa-check-circle"></i>
              <span>Completed for today</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                background: 'var(--psyber-info-bg)',
                border: '1px solid var(--psyber-info)',
                borderRadius: 'var(--psyber-radius-md)',
                fontSize: 'var(--psyber-font-size-sm)',
                color: 'var(--psyber-info)'
              }}>
                <i className="fas fa-clock"></i>
                <span>Checked in at {formatCheckInTime(checkInTime)}</span>
              </div>
              <Button
                variant="secondary"
                icon="fas fa-sign-out-alt"
                onClick={handleCheckOut}
                loading={isLoading}
                disabled={isLoading}
              >
                Check Out
              </Button>
            </div>
          )}

          {/* Other Quick Actions */}
          <Button
            variant="secondary"
            icon="fas fa-business-time"
            onClick={() => navigate('/timesheet')}
          >
            View My Timesheet
          </Button>
          <Button
            variant="secondary"
            icon="fas fa-calendar-check"
            onClick={handleOpenRequestModal}
            disabled={!userData || isSubmittingRequest}
          >
            Request Time Off
          </Button>
          <Button
            variant="ghost"
            icon="fas fa-bullhorn"
            onClick={() => navigate('/announcements')}
          >
            View Announcements
          </Button>

          {/* Register Employee - Super Admin Only */}
          {userData && userData.role === 'super_admin' && (
            <Button
              variant="secondary"
              icon="fas fa-user-plus"
              onClick={() => navigate('/register-employee')}
            >
              Register Employee
            </Button>
          )}
        </div>
      </Card>

      {/* Stats Widgets */}
      <div className="psyber-grid psyber-grid-4" style={{ gap: '1.5rem', marginBottom: '2rem' }}>
        <Card style={{ textAlign: 'center', position: 'relative' }}>
          <i className="fas fa-clock" style={{ fontSize: '2.5rem', color: 'var(--psyber-primary)', marginBottom: '0.75rem' }}></i>
          <h4 style={{ fontSize: 'var(--psyber-font-size-2xl)', margin: '0.5rem 0', color: 'var(--psyber-text)' }}>
            {isLoadingWeeklyHours ? '...' : `${weeklyHours.toFixed(1)}h`}
          </h4>
          <p style={{ margin: 0, color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-sm)' }}>Hours This Week</p>
          {!isLoadingWeeklyHours && weeklyWorkedHours > weeklyHours && weeklyWorkedHours > 0 && (
            <Badge
              variant="warning"
              icon="fas fa-exclamation-triangle"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                fontSize: 'var(--psyber-font-size-xs)'
              }}
            >
              {(weeklyWorkedHours - weeklyHours).toFixed(1)}h unallocated
            </Badge>
          )}
        </Card>
        <Card style={{ textAlign: 'center', position: 'relative' }}>
          <i className="fas fa-umbrella-beach" style={{ fontSize: '2.5rem', color: 'var(--psyber-accent)', marginBottom: '0.75rem' }}></i>
          <h4 style={{ fontSize: 'var(--psyber-font-size-2xl)', margin: '0.5rem 0', color: 'var(--psyber-text)' }}>
            {isLoadingLeaveBalance ? '...' : `${leaveBalance} days`}
          </h4>
          <p style={{ margin: 0, color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-sm)' }}>Leave Balance</p>

          {/* Super Admin Edit Button */}
          {userData && userData.role === 'super_admin' && (
            <button
              onClick={() => setIsEditLeaveModalOpen(true)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--psyber-primary)',
                fontSize: '1.2rem',
                padding: '0.25rem',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              title="Edit Leave Balance"
            >
              <i className="fas fa-edit"></i>
            </button>
          )}
        </Card>
        <Card style={{ textAlign: 'center' }}>
          <i className="fas fa-hourglass-half" style={{ fontSize: '2.5rem', color: 'var(--psyber-warning)', marginBottom: '0.75rem' }}></i>
          <h4 style={{ fontSize: 'var(--psyber-font-size-2xl)', margin: '0.5rem 0', color: 'var(--psyber-text)' }}>
            {isLoadingPending ? '...' : pendingCount}
          </h4>
          <p style={{ margin: 0, color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-sm)' }}>Pending Requests</p>
        </Card>
        <Card style={{ textAlign: 'center' }}>
          <i className="fas fa-bell" style={{ fontSize: '2.5rem', color: 'var(--psyber-info)', marginBottom: '0.75rem' }}></i>
          <h4 style={{ fontSize: 'var(--psyber-font-size-2xl)', margin: '0.5rem 0', color: 'var(--psyber-text)' }}>
            {isLoadingAnnouncements ? '...' : `${announcementsCount} new`}
          </h4>
          <p style={{ margin: 0, color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-sm)' }}>Recent Announcements</p>
        </Card>
      </div>

      {/* Module Cards Grid */}
      <Card style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="fas fa-th-large"></i> Portal Modules
        </h3>

        {/* Active Modules */}
        <div className="psyber-grid psyber-grid-4" style={{ gap: '1.5rem', marginBottom: '2rem' }}>
          {activeModules.map((module) => (
            <Card
              key={module.id}
              interactive
              onClick={() => navigate(module.route)}
            >
              <div style={{ textAlign: 'center' }}>
                <i className={module.icon} style={{
                  fontSize: '3rem',
                  color: 'var(--psyber-primary)',
                  marginBottom: '1rem',
                  display: 'block'
                }}></i>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: 'var(--psyber-font-size-lg)' }}>
                  {module.title}
                </h4>
                <p style={{
                  margin: 0,
                  color: 'var(--psyber-text-secondary)',
                  fontSize: 'var(--psyber-font-size-sm)',
                  lineHeight: 1.5
                }}>
                  {module.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Coming Soon Modules */}
        <div style={{
          paddingTop: '2rem',
          borderTop: '1px solid var(--psyber-card-border)',
          marginTop: '1rem'
        }}>
          <h4 style={{
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-clock"></i> Coming Soon
          </h4>
          <div className="psyber-grid psyber-grid-4" style={{ gap: '1.5rem' }}>
            {comingSoonModules.map((module) => (
              <Card
                key={module.id}
                className="coming-soon-card"
              >
                <div style={{ textAlign: 'center' }}>
                  <Badge
                    variant="warning"
                    icon="fas fa-clock"
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem'
                    }}
                  >
                    Coming Soon
                  </Badge>
                  <i className={module.icon} style={{
                    fontSize: '3rem',
                    color: 'var(--psyber-text-muted)',
                    marginBottom: '1rem',
                    display: 'block'
                  }}></i>
                  <h4 style={{
                    margin: '0 0 0.5rem 0',
                    fontSize: 'var(--psyber-font-size-lg)',
                    color: 'var(--psyber-text-secondary)'
                  }}>
                    {module.title}
                  </h4>
                  <p style={{
                    margin: 0,
                    color: 'var(--psyber-text-muted)',
                    fontSize: 'var(--psyber-font-size-sm)',
                    lineHeight: 1.5
                  }}>
                    {module.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </Card>

      <RequestModal
        isOpen={isRequestModalOpen}
        onClose={handleCloseRequestModal}
        onSubmit={handleSubmitTimeOffRequest}
        userData={userData}
        isLoading={isSubmittingRequest}
      />

      <EditLeaveBalanceModal
        isOpen={isEditLeaveModalOpen}
        onClose={() => setIsEditLeaveModalOpen(false)}
        onSuccess={handleLeaveBalanceUpdated}
      />
    </div>
  )
}

export default Dashboard
